import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';

import { EiaCases } from './pages/environment/EiaCases';
import { Referrals } from './pages/environment/Referrals';
import { Permits } from './pages/environment/Permits';
import { ProtectedAreas } from './pages/environment/ProtectedAreas';
import { Cites } from './pages/environment/Cites';
import { Mrv } from './pages/environment/Mrv';
import { EnvIncidents } from './pages/environment/EnvIncidents';

import { Operators } from './pages/energy/Operators';
import { PvApplications } from './pages/energy/PvApplications';
import { Rebates } from './pages/energy/Rebates';
import { FuelFacilityLicences } from './pages/energy/FuelFacilityLicences';
import { Complaints } from './pages/energy/Complaints';
import { TariffFilings } from './pages/energy/TariffFilings';
import { EnergyDatasets } from './pages/energy/EnergyDatasets';

import { DrdmDashboard } from './pages/drdm/Dashboard';
import { Alerts } from './pages/drdm/Alerts';
import { Shelters } from './pages/drdm/Shelters';
import { IncidentBoard } from './pages/drdm/IncidentBoard';
import { DamageAssessments } from './pages/drdm/DamageAssessments';
import { ReliefApplications } from './pages/drdm/ReliefApplications';
import { HazardLayers } from './pages/drdm/HazardLayers';

import { Shell, type NavHeader, type NavItem } from './ui/Shell';

const NAV: Array<NavItem | NavHeader> = [
  { header: 'Environment & Climate — M31' },
  { to: '/environment/eia-cases', label: 'EIA Cases', icon: '🌿' },
  { to: '/environment/referrals', label: 'M17 Referrals', icon: '📨' },
  { to: '/environment/permits', label: 'Environmental Permits', icon: '📋' },
  { to: '/environment/protected-areas', label: 'Protected Areas', icon: '🏞' },
  { to: '/environment/cites', label: 'CITES Permits', icon: '🐚' },
  { to: '/environment/mrv', label: 'MRV & NDC', icon: '📊' },
  { to: '/environment/incidents', label: 'Incident Reports', icon: '⚠' },

  { header: 'Energy & Utilities Regulation — M35' },
  { to: '/energy/operators', label: 'Utility Operators', icon: '🏢' },
  { to: '/energy/pv-applications', label: 'PV Solar Applications', icon: '☀' },
  { to: '/energy/rebates', label: 'Rebates', icon: '💳' },
  { to: '/energy/fuel-facility-licences', label: 'Fuel Facility Licences', icon: '⛽' },
  { to: '/energy/complaints', label: 'Utility Complaints', icon: '📞' },
  { to: '/energy/tariff-filings', label: 'Tariff Filings', icon: '📈' },
  { to: '/energy/energy-datasets', label: 'Energy Datasets', icon: '🔌' },

  { header: 'Risk & Disaster Management — M36' },
  { to: '/drdm/dashboard', label: 'Dashboard', icon: '◔' },
  { to: '/drdm/alerts', label: 'Alerts', icon: '🚨' },
  { to: '/drdm/shelters', label: 'Shelters', icon: '🏠' },
  { to: '/drdm/incident-board', label: 'Incident Board', icon: '🗺' },
  { to: '/drdm/damage-assessments', label: 'Damage Assessments', icon: '🏚' },
  { to: '/drdm/relief-applications', label: 'Relief Applications', icon: '🤝' },
  { to: '/drdm/hazard-layers', label: 'Hazard Layers', icon: '⛰' },
];

const ROLE_TO_LANDING: Record<string, string> = {
  ENV_OFFICER: '/environment/eia-cases',
  EIA_PANEL: '/environment/eia-cases',
  PA_RANGER: '/environment/protected-areas',
  CITES_AUTHORITY: '/environment/cites',
  ENERGY_OFFICER: '/energy/pv-applications',
  REBATE_ADMIN: '/energy/rebates',
  COMPLAINTS_DETERMINER: '/energy/complaints',
  DUTY_OFFICER: '/drdm/dashboard',
  EOC_COMMANDER: '/drdm/dashboard',
  FIELD_ASSESSOR: '/drdm/damage-assessments',
  RELIEF_ADMIN: '/drdm/relief-applications',
};

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // Land each persona somewhere useful for their primary duty rather than
  // always defaulting to the first workspace in the sidebar.
  const landing = persona.roles.map((r) => ROLE_TO_LANDING[r]).find(Boolean) ?? '/environment/eia-cases';

  return (
    <Routes>
      <Route element={<Shell title={persona.org} items={NAV} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="environment/eia-cases" element={<EiaCases />} />
        <Route path="environment/referrals" element={<Referrals />} />
        <Route path="environment/permits" element={<Permits />} />
        <Route path="environment/protected-areas" element={<ProtectedAreas />} />
        <Route path="environment/cites" element={<Cites />} />
        <Route path="environment/mrv" element={<Mrv />} />
        <Route path="environment/incidents" element={<EnvIncidents />} />

        <Route path="energy/operators" element={<Operators />} />
        <Route path="energy/pv-applications" element={<PvApplications />} />
        <Route path="energy/rebates" element={<Rebates />} />
        <Route path="energy/fuel-facility-licences" element={<FuelFacilityLicences />} />
        <Route path="energy/complaints" element={<Complaints />} />
        <Route path="energy/tariff-filings" element={<TariffFilings />} />
        <Route path="energy/energy-datasets" element={<EnergyDatasets />} />

        <Route path="drdm/dashboard" element={<DrdmDashboard />} />
        <Route path="drdm/alerts" element={<Alerts />} />
        <Route path="drdm/shelters" element={<Shelters />} />
        <Route path="drdm/incident-board" element={<IncidentBoard />} />
        <Route path="drdm/damage-assessments" element={<DamageAssessments />} />
        <Route path="drdm/relief-applications" element={<ReliefApplications />} />
        <Route path="drdm/hazard-layers" element={<HazardLayers />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
